import { createEdge, createNode } from '../../core/defaults';
import type { DiagramEdge, DiagramNode, EntityField } from '../../core/types';

export interface SqlSchemaImportResult {
  nodes: DiagramNode[];
  edges: DiagramEdge[];
  warnings: string[];
  tableCount: number;
}

interface ParsedForeignKey {
  column: string;
  targetTable: string;
  targetColumn: string;
}

interface ParsedTable {
  name: string;
  fields: EntityField[];
  foreignKeys: ParsedForeignKey[];
}

function cleanIdentifier(value: string): string {
  return value.trim().replace(/^[`"\[]/, '').replace(/[`"\]]$/, '').split('.').map((part) => part.replace(/^[`"\[]|[`"\]]$/g, '')).join('.');
}

function normalizeIdentifier(value: string): string {
  return cleanIdentifier(value).toLocaleLowerCase('pt-BR');
}

function stripComments(sql: string): string {
  return sql.replace(/\/\*[\s\S]*?\*\//g, ' ').replace(/--[^\r\n]*/g, ' ');
}

function splitDefinitions(body: string): string[] {
  const result: string[] = [];
  let current = '';
  let depth = 0;
  let quote: string | null = null;
  for (let index = 0; index < body.length; index += 1) {
    const char = body[index];
    if (quote) {
      current += char;
      if (char === quote) {
        if (body[index + 1] === quote) { current += body[index + 1]; index += 1; }
        else quote = null;
      }
      continue;
    }
    if (char === "'" || char === '"' || char === '`') { quote = char; current += char; continue; }
    if (char === '(') depth += 1;
    if (char === ')') depth = Math.max(0, depth - 1);
    if (char === ',' && depth === 0) {
      if (current.trim()) result.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }
  if (current.trim()) result.push(current.trim());
  return result;
}

function extractCreateTables(sql: string): Array<{ name: string; body: string }> {
  const clean = stripComments(sql);
  const tables: Array<{ name: string; body: string }> = [];
  const pattern = /CREATE\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?([`"\[]?[A-Za-z0-9_.-]+[`"\]]?)/gi;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(clean)) !== null) {
    const name = cleanIdentifier(match[1]);
    let open = clean.indexOf('(', pattern.lastIndex);
    if (open < 0) continue;
    let depth = 0;
    let quote: string | null = null;
    let close = -1;
    for (let index = open; index < clean.length; index += 1) {
      const char = clean[index];
      if (quote) {
        if (char === quote) {
          if (clean[index + 1] === quote) index += 1;
          else quote = null;
        }
        continue;
      }
      if (char === "'" || char === '"' || char === '`') { quote = char; continue; }
      if (char === '(') depth += 1;
      else if (char === ')') {
        depth -= 1;
        if (depth === 0) { close = index; break; }
      }
    }
    if (close < 0) continue;
    tables.push({ name, body: clean.slice(open + 1, close) });
    pattern.lastIndex = close + 1;
    if (tables.length >= 200) break;
  }
  return tables;
}

function parseColumn(definition: string): { field: EntityField; reference?: ParsedForeignKey } | null {
  if (/^(?:CONSTRAINT\b|PRIMARY\s+KEY\b|FOREIGN\s+KEY\b|UNIQUE\b|CHECK\b|KEY\b|INDEX\b)/i.test(definition)) return null;
  const match = definition.match(/^([`"\[]?[A-Za-z0-9_.-]+[`"\]]?)\s+([A-Za-z][A-Za-z0-9_]*(?:\s*\([^)]*\))?(?:\s+(?:UNSIGNED|VARYING|PRECISION))?)/i);
  if (!match) return null;
  const name = cleanIdentifier(match[1]);
  const dataType = match[2].replace(/\s+/g, ' ').trim().toUpperCase();
  const field: EntityField = {
    id: crypto.randomUUID(),
    name,
    dataType,
    primaryKey: /\bPRIMARY\s+KEY\b/i.test(definition),
    foreignKey: /\bREFERENCES\b/i.test(definition),
    nullable: !/\bNOT\s+NULL\b/i.test(definition) && !/\bPRIMARY\s+KEY\b/i.test(definition),
  };
  const referenceMatch = definition.match(/\bREFERENCES\s+([`"\[]?[A-Za-z0-9_.-]+[`"\]]?)\s*\(\s*([`"\[]?[A-Za-z0-9_.-]+[`"\]]?)\s*\)/i);
  const reference = referenceMatch ? { column: name, targetTable: cleanIdentifier(referenceMatch[1]), targetColumn: cleanIdentifier(referenceMatch[2]) } : undefined;
  return { field, reference };
}

function parseTable(name: string, body: string): ParsedTable {
  const definitions = splitDefinitions(body);
  const fields: EntityField[] = [];
  const foreignKeys: ParsedForeignKey[] = [];
  const primaryKeyNames = new Set<string>();

  for (const definition of definitions) {
    const primary = definition.match(/^(?:CONSTRAINT\s+[`"\[]?[A-Za-z0-9_.-]+[`"\]]?\s+)?PRIMARY\s+KEY\s*\(([^)]+)\)/i);
    if (primary) {
      splitDefinitions(primary[1]).forEach((item) => primaryKeyNames.add(normalizeIdentifier(item)));
      continue;
    }
    const foreign = definition.match(/^(?:CONSTRAINT\s+[`"\[]?[A-Za-z0-9_.-]+[`"\]]?\s+)?FOREIGN\s+KEY\s*\(\s*([`"\[]?[A-Za-z0-9_.-]+[`"\]]?)\s*\)\s*REFERENCES\s+([`"\[]?[A-Za-z0-9_.-]+[`"\]]?)\s*\(\s*([`"\[]?[A-Za-z0-9_.-]+[`"\]]?)\s*\)/i);
    if (foreign) {
      foreignKeys.push({ column: cleanIdentifier(foreign[1]), targetTable: cleanIdentifier(foreign[2]), targetColumn: cleanIdentifier(foreign[3]) });
      continue;
    }
    const column = parseColumn(definition);
    if (!column) continue;
    fields.push(column.field);
    if (column.reference) foreignKeys.push(column.reference);
    if (fields.length >= 300) break;
  }

  for (const field of fields) {
    if (primaryKeyNames.has(normalizeIdentifier(field.name))) { field.primaryKey = true; field.nullable = false; }
    if (foreignKeys.some((foreign) => normalizeIdentifier(foreign.column) === normalizeIdentifier(field.name))) field.foreignKey = true;
  }
  return { name, fields, foreignKeys };
}

export function importSqlSchema(sql: string): SqlSchemaImportResult {
  if (sql.length > 5_000_000) throw new Error('O script SQL excede o limite local de 5 MB.');
  const extracted = extractCreateTables(sql);
  if (extracted.length === 0) throw new Error('Nenhum comando CREATE TABLE reconhecido foi encontrado.');
  const warnings: string[] = [];
  const parsed = extracted.map((table) => parseTable(table.name, table.body));
  const nodes: DiagramNode[] = parsed.map((table) => {
    const node = createNode('erdEntity');
    node.text = table.name;
    node.entityFields = table.fields;
    node.width = 260;
    node.height = Math.max(120, 58 + Math.min(12, table.fields.length) * 22);
    if (table.fields.length === 0) warnings.push(`Tabela “${table.name}”: nenhum campo reconhecido.`);
    if (table.fields.length >= 300) warnings.push(`Tabela “${table.name}”: importação limitada a 300 campos.`);
    return node;
  });
  const nodeByTable = new Map(parsed.map((table, index) => [normalizeIdentifier(table.name), nodes[index]]));
  const edges: DiagramEdge[] = [];
  parsed.forEach((table, index) => {
    const child = nodes[index];
    for (const foreign of table.foreignKeys) {
      const parent = nodeByTable.get(normalizeIdentifier(foreign.targetTable));
      if (!parent) { warnings.push(`FK ${table.name}.${foreign.column}: tabela referenciada “${foreign.targetTable}” não foi encontrada.`); continue; }
      const edge = createEdge(parent.id, child.id);
      edge.arrowEnd = false;
      edge.sourceCardinality = '1';
      edge.targetCardinality = '0..N';
      edge.label = foreign.column;
      edges.push(edge);
    }
  });
  if (extracted.length >= 200) warnings.push('A importação foi limitada às primeiras 200 tabelas para preservar a responsividade do navegador.');
  return { nodes, edges, warnings: Array.from(new Set(warnings)).slice(0, 100), tableCount: nodes.length };
}
